import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/app/StatusBadge";
import { Wrench, PackageOpen } from "lucide-react";
import { ScheduleWhatsAppButton } from "@/components/whatsapp/ScheduleWhatsAppButton";
import { SendWhatsAppButton } from "@/components/whatsapp/WaComposer";
import { PageHeader } from "@/components/os/PagePattern";
import { QuickActionGroup } from "@/components/quickactions/QuickActionGroup";

/** Build a clean, non-duplicated asset label.
 * Avoids things like "Apple Apple MacBook Air …" when brand and model both
 * begin with the manufacturer name. */
function assetLabel(a) {
  const brand = (a.brand || a.manufacturer || "").trim();
  const model = (a.model || "").trim();
  const head = brand && model && !model.toLowerCase().startsWith(brand.toLowerCase())
    ? `${brand} ${model}`
    : (model || brand || "Unknown device");
  const tail = a.serial || a.asset_tag || a.sku || a.id?.slice(0, 8);
  return tail ? `${head} · ${tail}` : head;
}

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return v; }
}

function fmtMoney(v, cur = "INR") {
  if (v == null || v === "") return null;
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v);
  } catch { return `${cur} ${v}`; }
}

export default function Service() {
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    asset_id: "",
    problem: "",
    warranty_type: "out_of_warranty",
    vendor: "",
    estimate_cost: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get("/assets/", { params: { limit: 200 } }),
        api.get("/service-cases", { params: { limit: 100 } }),
      ]);
      setAssets(a.data.items || []);
      setRows(s.data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Fast asset lookup for the table
  const assetById = useMemo(() => {
    const m = new Map();
    assets.forEach((a) => m.set(a.id, a));
    return m;
  }, [assets]);

  const create = async () => {
    if (!form.asset_id || !form.problem?.trim()) {
      toast.error("Select an asset and describe the problem");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        asset_id: form.asset_id,
        problem: form.problem.trim(),
        warranty_type: form.warranty_type,
      };
      if (form.vendor?.trim()) payload.vendor = form.vendor.trim();
      if (form.estimate_cost !== "" && form.estimate_cost != null) {
        payload.estimate_cost = Number(form.estimate_cost);
      }
      await api.post("/service-cases", payload);
      toast.success("Service case created");
      setForm({ asset_id: "", problem: "", warranty_type: "out_of_warranty", vendor: "", estimate_cost: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open case");
    } finally {
      setSubmitting(false);
    }
  };

  const close = async (id) => {
    try {
      await api.post(`/service-cases/${id}/close`);
      toast.success("Case closed");
      load();
    } catch { toast.error("Failed to close case"); }
  };

  return (
    <div className="space-y-4 max-w-[1500px]">
      <PageHeader eyebrow="Service" title="Service tickets" description="Open a case, diagnose, repair and return devices to active use."
        secondary={<ScheduleWhatsAppButton kind="ticket" />} />
      <QuickActionGroup module="service" />

      {/* Open case form */}
      <div className="bg-white border border-slate-200 rounded-md p-5 grid md:grid-cols-6 gap-3">
        <div className="md:col-span-3">
          <Label className="text-xs">Asset<span className="text-rose-500 ml-1">*</span></Label>
          <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
            <SelectTrigger className="h-10 mt-1" data-testid="svc-asset"><SelectValue placeholder="Select asset…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {assets.map((a) => (
                <SelectItem key={a.id} value={a.id}>{assetLabel(a)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-3">
          <Label className="text-xs">Warranty type<span className="text-rose-500 ml-1">*</span></Label>
          <Select value={form.warranty_type} onValueChange={(v) => setForm({ ...form, warranty_type: v })}>
            <SelectTrigger className="h-10 mt-1" data-testid="svc-warranty"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="warranty">Warranty</SelectItem>
              <SelectItem value="applecare">AppleCare+</SelectItem>
              <SelectItem value="out_of_warranty">Out of warranty</SelectItem>
              <SelectItem value="third_party">Third-party</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-3">
          <Label className="text-xs">Service vendor / partner</Label>
          <Input
            className="mt-1 h-10"
            placeholder="e.g. Apple ASP · Chennai"
            value={form.vendor}
            onChange={(e) => setForm({ ...form, vendor: e.target.value })}
            data-testid="svc-vendor"
          />
        </div>

        <div className="md:col-span-3">
          <Label className="text-xs">Estimate cost</Label>
          <Input
            className="mt-1 h-10"
            type="number"
            min="0"
            step="1"
            placeholder="e.g. 12000"
            value={form.estimate_cost}
            onChange={(e) => setForm({ ...form, estimate_cost: e.target.value })}
            data-testid="svc-estimate"
          />
        </div>

        <div className="md:col-span-6">
          <Label className="text-xs">Problem description<span className="text-rose-500 ml-1">*</span></Label>
          <Textarea
            className="mt-1"
            placeholder="What's happening with the device? What was tried?"
            value={form.problem}
            onChange={(e) => setForm({ ...form, problem: e.target.value })}
            data-testid="svc-problem"
          />
        </div>

        <div className="md:col-span-6 flex justify-end">
          <Button
            className="bg-slate-900 hover:bg-slate-800 text-white"
            disabled={submitting || !form.asset_id || !form.problem?.trim()}
            onClick={create}
            data-testid="svc-open-case"
          >
            <Wrench className="h-4 w-4 mr-1.5" /> {submitting ? "Opening…" : "Open case"}
          </Button>
        </div>
      </div>

      {/* Cases table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[10px] uppercase text-slate-500">
              <th className="px-3 py-2.5 text-left">Case</th>
              <th className="px-3 py-2.5 text-left">Opened</th>
              <th className="px-3 py-2.5 text-left">Asset</th>
              <th className="px-3 py-2.5 text-left">Problem</th>
              <th className="px-3 py-2.5 text-left">Warranty</th>
              <th className="px-3 py-2.5 text-left">Vendor</th>
              <th className="px-3 py-2.5 text-right">Estimate</th>
              <th className="px-3 py-2.5 text-left">Status</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-xs text-slate-500">Loading service cases…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-10 text-center">
                <PackageOpen className="h-6 w-6 mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
                <div className="text-sm font-medium text-slate-700">No service cases yet</div>
                <div className="text-xs text-slate-500 mt-0.5">Cases you open above will appear here.</div>
              </td></tr>
            )}
            {!loading && rows.map((r) => {
              const a = assetById.get(r.asset_id);
              const label = a ? assetLabel(a) : (r.asset_id ? r.asset_id.slice(0, 8) : "—");
              const estimate = fmtMoney(r.estimate_cost, r.currency || (a?.currency) || "INR");
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`svc-row-${r.case_code}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.case_code}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-xs">
                    <div className="font-medium text-slate-800">{label}</div>
                    {a?.asset_tag && <div className="text-[10px] text-slate-500 font-mono">{a.asset_tag}</div>}
                  </td>
                  <td className="px-3 py-2 text-slate-700 max-w-[420px] truncate" title={r.problem}>{r.problem}</td>
                  <td className="px-3 py-2 capitalize text-xs">{(r.warranty_type || "").replace(/_/g, " ")}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.vendor || "—"}</td>
                  <td className="px-3 py-2 text-xs text-right font-mono">{estimate || "—"}</td>
                  <td className="px-3 py-2"><div className="flex items-center gap-2"><StatusBadge value={r.status} /><SendWhatsAppButton record={{ kind: "ticket", id: r.id, code: r.case_code }} purpose="SERVICE" label="WhatsApp" testid={`send-whatsapp-ticket-${r.case_code}`} /></div></td>
                  <td className="px-3 py-2 text-right">
                    {r.status !== "closed" ? (
                      <Button size="sm" variant="outline" onClick={() => close(r.id)} data-testid={`svc-close-${r.case_code}`}>Close</Button>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
